import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLang } from '../../i18n';
import { useEscape } from '../../hooks/useEscape';
import { useRowActions } from './rowActions';

/** Desktop ⋯ menu of a board row — status, urgent, move, fix Telugu, delete. */
export function RowMenu({ item, list, actions, moveTargets }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useEscape(close, open);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open, close]);
  return (
    <div className="do-menu" ref={ref}>
      <button className="do-menu__trigger" onClick={() => setOpen(o => !o)}
        aria-haspopup="menu" aria-expanded={open} aria-label={t('menu.actions')} title={t('menu.actions')}>⋯</button>
      {open && <MenuPop item={item} list={list} actions={actions} moveTargets={moveTargets} anchor={ref} close={close} />}
    </div>
  );
}

// Mounted only while open, so a closed menu costs nothing per row.
function MenuPop({ item, list, actions, moveTargets, anchor, close }) {
  const { t, ln } = useLang();
  const a = useRowActions({ item, list, actions, moveTargets });
  const popRef = useRef(null);
  const [flip, setFlip] = useState(null);   // {up, maxH} — keep the whole menu (incl. Delete) on screen
  // If the menu would run past the bottom of the screen, flip it up (or cap its height to
  // the space available) so every option — including Delete — is reachable.
  useLayoutEffect(() => {
    const trig = anchor.current, pop = popRef.current;
    if (!trig || !pop) return;
    const r = trig.getBoundingClientRect();
    const needed = pop.scrollHeight + 8;
    const below = window.innerHeight - r.bottom - 8;
    // The site's sticky header covers the top of the screen — room above starts below it.
    const header = document.querySelector('.top-bar');
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const above = r.top - top - 8;
    if (needed <= below) setFlip({ up: false, maxH: 0 });
    else if (above > below) setFlip({ up: true, maxH: above });
    else setFlip({ up: false, maxH: below });
  }, [anchor]);
  const pick = run => () => { run(); close(); };
  return (
    <div ref={popRef} className={`do-menu__pop${flip && flip.up ? ' do-menu__pop--up' : ''}`} role="menu"
      style={flip && flip.maxH ? { maxHeight: flip.maxH } : undefined}>
      <button role="menuitem" className="do-menu__item do-menu__item--danger" style={{ '--sc': 'var(--red)' }}
        onClick={pick(a.remove)} aria-label={t('menu.remove', { name: ln(item.name) })}>
        <span className="do-menu__ico">🗑</span>{t('menu.delete')}
      </button>
      <div className="do-menu__sep" />
      {a.statuses.map(s => (
        <button key={s.key} role="menuitem" className={`do-menu__item${s.on ? ' do-menu__item--on' : ''}`}
          style={{ '--sc': s.color }} onClick={pick(s.run)}>
          <span className="do-menu__ico">{s.icon}</span>{s.label}
        </button>
      ))}
      {a.statuses.length > 0 && <div className="do-menu__sep" />}
      <button role="menuitem" className={`do-menu__item${a.urgent.on ? ' do-menu__item--on' : ''}`}
        style={{ '--sc': 'var(--amber)' }} onClick={pick(a.urgent.run)}>
        <span className="do-menu__ico">{a.urgent.icon}</span>{a.urgent.label}
      </button>
      <div className="do-menu__sep" />
      <div className="do-menu__label">{t('menu.moveToList')}</div>
      {a.moves.map(m => (
        <button key={m.id} role="menuitem" className="do-menu__item" style={{ '--sc': m.color }} onClick={pick(m.run)}>
          <span className="do-menu__ico">{m.icon}</span>{m.label}
        </button>
      ))}
      {a.fixTelugu && (
        <>
          <div className="do-menu__sep" />
          <button role="menuitem" className="do-menu__item" style={{ '--sc': 'var(--sub)' }} onClick={pick(a.fixTelugu)}>
            <span className="do-menu__ico">🌐</span>{t('menu.fixTelugu')}
          </button>
        </>
      )}
    </div>
  );
}
